import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntil } from 'rxjs/operators';
import * as moment from 'moment';
import { Store } from '@ngrx/store';
import * as fromRoot from '../../../../../../../state/app.state';
import {
  ClearDailyReportRequest,
  FetchDailyReportRequest,
  FetchDailyReportsByDateRequest,
  FetchIssueTypesRequest,
  FetchProjectDetailsRequest,
} from '../../../../../core/projects.actions';
import { ProjectsService } from '../../../../../core/projects.service';
import { DailyReportService } from '../daily-report.service';
import { MatDialog as MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../../../../../../shared/components';
import { FetchAppDataRequest } from '../../../../../../../shared/core/shared.actions';
import { appDataDetails } from '../../../../../../../shared/core/shared.selectors';

@Component({
    selector: 'app-daily-report-wrapper',
    templateUrl: './daily-report-wrapper.component.html',
    styleUrls: ['./daily-report-wrapper.component.scss'],
    standalone: false
})
export class DailyReportWrapperComponent implements OnInit, OnDestroy {
  date = '';
  reportId = '';
  triggerDateChange = 0;
  appDataDetails: any;
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private store: Store<fromRoot.State>,
    private route: ActivatedRoute,
    private router: Router,
    private projectsService: ProjectsService,
    private dailyReportService: DailyReportService,
    public dialog: MatDialog,
  ) {
    this.store.dispatch(FetchIssueTypesRequest({
      payload: {
        projectId: this.route.snapshot.params['projectId'],
      },
    }));

    this.store.dispatch(FetchAppDataRequest());

    this.store.select(appDataDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data: any) => {
        this.appDataDetails = data;
      });
  }

  ngOnInit() {
    this.store.dispatch(FetchProjectDetailsRequest({
      payload: {
        projectId: this.route.snapshot.params.projectId,
        qp: {
          include: [
            'online',
            'lookups',
            'activity_types',
            'activity_codes',
            'visual_inspection_specs',
            'cover_photo',
            'owner_organization',
            'delivery_tracking_fields',
            'materials',
            'material_types',
            'gauge_numbers',
            'material_sources',
            'sub_contractors',
            {
              control_forms: [
                'fields',
                'control_form',
                'pictures',
                'videos',
                {
                  lines: [
                    'lines',
                    'field_values',
                    'pictures',
                    'videos',

                  ],
                },
                {
                  signatures: [
                    'signatures',
                    'pictures',
                  ],
                },
              ],
            },
            {
              bid_items: [
                'rollup_fields',
                'materials',
                {
                  fields: ['recent_fields'],
                },
                {
                  headings: [
                    'stations',
                    'alignment',
                  ],
                },
              ],
            },
            {
              internal_tests: [
                'bid_item_ids',
                'site_ids',
                'fields',
              ],
            },
            {
              sites: [
                'geometry',
                'delivery_tracking_fields',
                {
                  headings: ['stations'],
                },
              ],
            },
            {
              bidSheet: [
                {
                  bidItems: [
                    { bid_item_sub_contractor: ['sub_contractor'] },
                    {
                      bid_item_material_set: [
                        {
                          bid_item_material_set_material: [
                            { material: ['groups'] },
                          ],
                        },
                      ],
                    },
                    { groups: ['fields'] },
                  ],
                },
              ],
            },
          ],
        },
      },
    }));

    this.route.queryParams
      .pipe(takeUntil(this.onDestroy))
      .subscribe(qp => {
        if (
          qp.date === undefined ||
          (
            qp.date &&
            !moment(qp.date, 'YYYY-MM-DD').isValid()
          )
        ) {
          this.router.navigate([], {
            queryParams: {
              date: moment().format('YYYY-MM-DD'),
            },
            queryParamsHandling: 'merge',
          });
        } else if (
          qp.date &&
          moment(qp.date, 'YYYY-MM-DD').isValid() &&
          qp.date !== this.date
        ) {
          this.date = qp.date;
          this.fetchReportsList();
        }

        if (!qp.reportId) {
          this.reportId = '';
          if (qp.fs === undefined) {
            this.store.dispatch(ClearDailyReportRequest());
          }
        } else {
          if (qp.reportId !== this.reportId) {
            this.reportId = qp.reportId;
            this.fetchReportById(this.reportId);
          }
        }
      });

    this.dailyReportService.fetchDailyReportById
      .pipe(takeUntil(this.onDestroy))
      .subscribe((options?: any) => {
        this.fetchReportById(this.reportId, options);
      });
  }

  fetchReportsList() {
    this.router.navigate([], {
      queryParams: {
        date: this.date,
        idx: null,
      },
      queryParamsHandling: 'merge',
    }).then(() => {
      if (this.route.snapshot.queryParams.fs === undefined && !this.route.snapshot.queryParams.reportId) {
        this.store.dispatch(ClearDailyReportRequest());
      }
      this.store.dispatch(FetchDailyReportsByDateRequest({
        payload: {
          project: { id: this.route.snapshot.params.projectId },
          site: this.route.snapshot.params.siteId,
          qp: {
            date: this.date,
            include: [
              'createdBy',
              'statusText',
              'site_id',
              'next-actions',
            ],
            total: 1000,
          },
        },
      }));
    });
  }

  handleDateChange(date) {
    const isFormDirty = this.projectsService.isAnyFormDirty.getValue();

    if (isFormDirty) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        disableClose: true,
        data: new ConfirmDialogModel(
          'Unsaved changes',
          `There are unsaved changes.<br/>Are you sure you want to discard?`,
        ),
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.projectsService.closeRightPanel.next();
          this.projectsService.isAnyFormDirty.next(false);
          this.router.navigate([], {
            queryParams: {
              date: moment(date).format('YYYY-MM-DD'),
              reportId: null,
            },
            queryParamsHandling: 'merge',
          }).then(() => {
            this.projectsService.isAnyFormDirty.next(false);
          });
        } else {
          this.triggerDateChange = new Date().getTime();
        }
      });
    } else {
      this.router.navigate([], {
        queryParams: {
          date: moment(date).format('YYYY-MM-DD'),
          reportId: null,
        },
        queryParamsHandling: 'merge',
      });
    }
  }

  fetchReportById(reportId: string, options?: any) {
    this.store.dispatch(ClearDailyReportRequest());
    this.store.dispatch(FetchDailyReportRequest({
      payload: {
        fromDialog: !!options?.fromDialog,
        qp: {
          include: [
            {
              createdBy: ['roles'],
            },
            {
              site: ['project'],
            },
            {
              bid_items: [
                'map_feature_link',
                {
                  bid_item: [
                    'stations',
                    'fields',
                    'materials',
                  ],
                },
                'pictures',
                'videos',
                'attachments',
                'field_values',
                'station',
                'material',
                {
                  annotations: ['drawing'],
                },
                'heading',
                {
                  sources: [
                    'source_group_id',
                  ],
                },
                'sub_contractor',
                {
                  materials: [
                    'bid_item_material_set',
                    'bid_item_material_set_material',
                    'material',
                    'group_values',
                  ],
                },
                'bid_item_material_set',
              ],
            },
            {
              activity_codes: [
                'map_feature_link',
                {
                  bid_item: [
                    'stations',
                    'fields',
                    'materials',
                  ],
                },
                'pictures',
                'videos',
                'attachments',
                'field_values',
                'station',
                'material',
                {
                  annotations: ['drawing'],
                },
                {
                  lines: [
                    'activity_code',
                  ],
                },
              ],
            },
            {
              material_deliveries: [
                'map_feature_link',
                // Delivery tickets: every save re-sends them, else the API unlinks them.
                'tickets',
                {
                  bid_item: [
                    'stations',
                    'fields',
                    'materials',
                  ],
                },
                'pictures',
                'videos',
                'attachments',
                'field_values',
                'station',
                'material',
                {
                  annotations: ['drawing'],
                },
              ],
            },
            {
              material_tests: [
                {
                  bid_item: [
                    'stations',
                    'fields',
                    'materials',
                  ],
                },
                'pictures',
                'videos',
                'attachments',
                'field_values',
                'station',
                'material',
                {
                  annotations: ['drawing'],
                },
                'createdBy',
                {
                  annotation: ['drawing'],
                },
                'bid_item',
                'lab',
                'material_test',
                {
                  material_test_report: [
                    'pictures',
                    'attachments',
                    'videos',
                  ],
                },
                {
                  material_test_reports: [
                    'pictures',
                    'attachments',
                    'videos',
                  ],
                },
                'location',
                'pictures',
                'videos',
                'activity_log',
                {
                  inspection_detail: [
                    'pictures',
                    'videos',
                  ],
                },
                'site',
                'original_test',
              ],
            },
            {
              internal_tests: [
                'map_feature_link',
                {
                  bid_item: [
                    'stations',
                    'fields',
                    'materials',
                  ],
                },
                'pictures',
                'videos',
                'attachments',
                'field_values',
                'station',
                'material',
                {
                  annotations: ['drawing'],
                },
                {
                  internal_test: [
                    'bid_items',
                    'fields',
                  ],
                },
                'createdBy',
                'record_time',
                'heading',
                'comment',
                'latitude',
                'longitude',
                'id',
                'pictures',
                'station',
                'field_values',
                'issues',
              ],
            },
            {
              testing_forms: [
                {
                  bid_item: [
                    'stations',
                    'fields',
                    'materials',
                  ],
                },
                'pictures',
                'videos',
                'attachments',
                'field_values',
                'station',
                'material',
                {
                  annotations: ['drawing'],
                },
                'report_form_header',
                'report_form_lines',
              ],
            },
            {
              issues: [
                'map_feature_link',
                {
                  bid_item: [
                    'stations',
                    'fields',
                    'materials',
                  ],
                },
                'pictures',
                'videos',
                'attachments',
                'field_values',
                'station',
                'material',
                {
                  annotations: ['drawing'],
                },
              ],
            },
            {
              labor_equipment: [
                {
                  bid_item: [
                    'stations',
                    'fields',
                    'materials',
                  ],
                },
                'pictures',
                'videos',
                'attachments',
                'field_values',
                'station',
                'material',
                {
                  annotations: ['drawing'],
                },
              ],
            },
            {
              activities: [
                'map_feature_link',
                {
                  bid_item: [
                    'stations',
                    'fields',
                    'materials',
                  ],
                },
                'pictures',
                'videos',
                'attachments',
                'field_values',
                'station',
                'material',
                {
                  annotations: ['drawing'],
                },
                {
                  annotation: ['drawing'],
                },
              ],
            },
            {
              control_forms: [
                'map_feature_link',
                {
                  bid_item: [
                    'stations',
                    'fields',
                    'materials',
                  ],
                },
                'pictures',
                'videos',
                'attachments',
                'field_values',
                'station',
                'material',
                {
                  annotations: ['drawing'],
                },
                'control_form',
                {
                  lines: [
                    'field_values',
                    'pictures',
                    'videos',
                  ],
                },
                {
                  // `pictures` carries the drawn-signature image attachment
                  // (original_url/thumb_url/...). Without it, drawn signatures
                  // come back with an empty signature_text and nothing to render.
                  signatures: [
                    'signature_text',
                    'pictures',
                  ],
                },
                'pictures',
                'videos',
              ],
            },
            {
              control_point_forms: [
                {
                  bid_item: [
                    'stations',
                    'fields',
                    'materials',
                  ],
                },
                'pictures',
                'videos',
                'attachments',
                'field_values',
                'station',
                'material',
                {
                  annotations: ['drawing'],
                },
              ],
            },
            {
              notes: [
                'createdBy',
                'updatedBy',
              ],
            },
            {
              labor: [
                'sub_contractor',
                'bid_item',
                'job_code',
                {
                  labor: [
                    'default_job_code',
                    'sub_contractor',
                  ],
                },
                'field_values',
                'pictures',
                'videos',
                {
                  annotations: ['drawing'],
                },
                {
                  equipment: ['equipment_type'],
                },
                'source',
              ],
            },
            {
              equipment: [
                'sub_contractor',
                'bid_item',
                'job_code',
                {
                  labor: [
                    'default_job_code',
                    'sub_contractor',
                  ],
                },
                'field_values',
                'pictures',
                'videos',
                {
                  annotations: ['drawing'],
                },
                {
                  equipment: ['equipment_type'],
                },
                'source',
              ],
            },
            {
              sub_contractors: [
                'sub_contractor',
                'field_values',
                'pictures',
                'videos',
              ],
            },
            {
              issues: [
                'createdBy',
                {
                  annotation: ['drawing'],
                },
                'bid_item',
                'entity',
                'pictures',
                'attachments',
                'material_tests',
                'map_feature_link',
                'videos',
                'bid_items',
                'closedBy',
              ],
            },
            {
              lastApprovalAction: ['actor'],
            },
            {
              lastWithdrawalAction: ['actor'],
            },
            'next-actions',
            'statusText',
            'work_condition_code',
          ],
        },
        reportId,
      },
    }));
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
